import React, { useState } from 'react'
import { Button, message, Modal, Table, Tag, Tooltip } from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { deleteBrandUser, editBrandUser, quitAllBrands } from '../../apis/brand'
import { deleteGroupUser, fetchGroupUserList, modifyGroupUser } from '../../apis/group'
import { fetchUser } from '../../apis/user'
import MainTable from '../../components/Basic/MainTable'
import { UserBrandTable } from './group.styled'
import PopupConfirm from '../../components/Basic/PopupConfirm'
import AddBrandToUserModal from './modals/AddBrandToUserModal'
import EditUserModal from './modals/EditUserModal'

const ClientUserTable = props => {
  const { params, brandRoleOptions, brandsOption: allBrand } = props
  const { t: ln_t } = useTranslation()
  const user = useSelector((state) => state.user)
  const { handleFetchParams, fetchParams } = params

  const [addBrand, setAddBrand] = useState(false)
  const [modalData, setModalData] = useState({})
  const [userBrandList, setUserBrandList] = useState({})
  const [editUser, setEditUser] = useState(false)

  const apiFunc = async () => fetchGroupUserList(fetchParams)
  const getUserBrandList = async (userId) => {
    const result = await fetchUser(userId, { includes: 'brands' })
    if (result?.status === 200) {
      const allBrands = result?.data?.brands.filter((brand) => brand?.brandUser?.isDelete === 'no')
      const brandsUnderNowGroup = allBrands.filter((brand) => brand?.groupId === user?.nowGroup)
      setUserBrandList({ ...userBrandList, [userId]: brandsUnderNowGroup })
    }
  }

  const handleDeleteUserFromGroup = ({ id }) => {
    Modal.confirm({
      title: `${ln_t('user.removeUser')}`,
      content: `${ln_t('user.removeUserFromThisGroup')}?`,
      onOk: async () => {
        const quitBrand = await quitAllBrands(user?.nowGroup, id)
        const quitGroup = await deleteGroupUser(user?.nowGroup, id)
        if (quitBrand?.status === 200 && quitGroup.status === 204) {
          message.success(ln_t('message.success.remove'))
          handleFetchParams('add', {})
        }
      }
    })
  }

  const handleEditUserRole = async record => {
    setEditUser(true)
    setModalData({ ...record, roles: record?.roles?.filter(i => i.groupId === user?.nowGroup) })
  }

  const handleAddUserToBrand = (record) => {
    getUserBrandList(record?.id)
    setModalData(record)
    setAddBrand(true)
  }

  const handleChangeUserBrand = async (record, updateParams) => {
    const { brandId, userId } = record?.brandUser || {}
    const res = await editBrandUser(brandId, userId, updateParams)
    if (res?.status === 200) {
      message.success(ln_t('message.success.modify'))
      getUserBrandList(userId)
    }
  }

  const handleChangeUserGroup = async (record, updateParams) => {
    const res = await modifyGroupUser(user?.nowGroup, record?.id, updateParams)
    if (res?.status === 200) {
      message.success(ln_t('message.success.modify'))
      handleFetchParams('add', {})
    }
  }

  const handleDeleteUserBrand = async record => {
    const { brandUser, name } = record || {}
    const { brandId, userId } = brandUser || {}
    Modal.confirm({
      title: `${ln_t('user.removeBrand')}`,
      content: `${ln_t('user.removeBrandMessage')} ${name}?`,
      onOk: async () => {
        const result = await deleteBrandUser(brandId, userId)
        if (result?.status === 204) {
          message.success(ln_t('message.success.remove'))
          getUserBrandList(userId)
        }
      }
    })
  }

  const userColumns = [
    { title: ln_t('user.name'), dataIndex: 'name', sorter: true },
    { title: ln_t('user.email'), dataIndex: 'email', sorter: true },
    { title: ln_t('user.role'), dataIndex: 'roles', render: (roles) => roles?.filter(i => i.groupId === user?.nowGroup)?.map((role) => <Tag key={role.id}>{role.name}</Tag>) },
    {
      title: ln_t('user.status'),
      dataIndex: 'status',
      render: (e, record) => {
        const status = record?.groups?.find(i => i.id === user?.nowGroup)?.groupUser?.status
        return <PopupConfirm type="status" checked={status} onConfirm={() => handleChangeUserGroup(record, { status: status ? 0 : 1 })} />
      }
    },
    { title: ln_t('util.createdAt'), dataIndex: 'createdAt', sorter: true, render: (time) => dayjs(time).format('YYYY-MM-DD') },
    { title: ln_t('util.updatedAt'), dataIndex: 'updatedAt', sorter: true, render: (time) => dayjs(time).format('YYYY-MM-DD') },
    {
      title: ln_t('util.action'),
      dataIndex: 'id',
      render: (id, record) => (
        <div className="action-btn">
          <Tooltip title={ln_t('user.addToBrand')}>
            <Button size="small" icon={<PlusOutlined />} onClick={() => handleAddUserToBrand(record)} />
          </Tooltip>
          <Tooltip title={ln_t('user.editRole')}>
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEditUserRole(record)} />
          </Tooltip>
          <Button size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteUserFromGroup(record)} />
        </div>
      )
    }
  ]

  const brandColumns = [
    { title: ln_t('user.brand'), dataIndex: 'name' },
    // {
    //   title: `${ln_t('user.brand')} ${ln_t('user.role')}`,
    //   dataIndex: 'brandUser',
    //   render: (e, record) => <PopupConfirm type="role" checked={e.role === 'admin'} onConfirm={() => handleChangeUserBrand(record, { role: e.role === 'admin' ? 'member' : 'admin' })} />
    // },
    {
      title: `${ln_t('user.brand')} ${ln_t('util.status')}`,
      dataIndex: 'brandUser',
      render: (e, record) => <PopupConfirm type="status" checked={e.status} onConfirm={() => handleChangeUserBrand(record, { status: e.status ? 0 : 1 })} />
    },
    {
      title: ln_t('util.action'),
      dataIndex: 'id',
      render: (id, record) => <Button size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteUserBrand(record)}>{ln_t('user.removeBrand')}</Button>
    }
  ]

  const expandable = {
    onExpand: async (expanded, { id }) => { if (expanded && !userBrandList[id]) getUserBrandList(id) },
    expandedRowRender: record => (
      <UserBrandTable>
        <Table
          columns={brandColumns}
          sortDirections={['ascend', 'descend', 'ascend']}
          rowKey={record => record.id}
          dataSource={userBrandList[record.id] || []}
          pagination={false}
          scroll={{ x: 'true' }}
          showSorterTooltip={false}
        />
      </UserBrandTable>
    )
  }

  const addBrandSuccessCallback = userId => {
    message.success(ln_t('message.success.add'))
    getUserBrandList(userId)
  }

  const editUserSuccessCallback = userId => {
    message.success(ln_t('message.success.modify'))
    handleFetchParams('add', {})
  }

  return (
    <>
      <MainTable
        columns={userColumns}
        rowKey="id"
        apiFunc={apiFunc}
        params={params}
        expandable={expandable}
      />
      <AddBrandToUserModal
        show={addBrand}
        setShow={setAddBrand}
        data={modalData}
        successCallback={addBrandSuccessCallback}
        brandOptions={allBrand}
        brandRoleOptions={brandRoleOptions}
      />
      <EditUserModal
        show={editUser}
        setShow={setEditUser}
        data={modalData}
        successCallback={editUserSuccessCallback}
      />
    </>
  )
}

export default ClientUserTable
